import { createTheme, Theme, ThemeOptions, PaletteMode } from '@mui/material/styles';
import { colors, colorUtils } from './oklch_colors';

export { colors, colorUtils };

// 1. Theme interface extensions
declare module '@mui/material/styles' {

  interface PaletteColor {
    text: string;
    glow: string;
  }

  interface SimplePaletteColorOptions {
    text?: string;
    glow?: string;
  }

  interface TypeText {
    primary: string;
    secondary: string;
    disabled: string;
    tertiary: string;
    brand: string;
  }

  interface TypographyPropsColorOverrides {
    'text.brand': true;
    'text.tertiary': true;
  }

  interface TypeBackground {
    default: string;
    paper: string;
    secondary: string;
    tertiary: string;
    elevated: string;
    inset: string;
  }

  interface Palette {
    border: {
      light: string;
      main: string;
      strong: string;
      focus: string;
    };
    glow: string;
    interactive: {
      hover: string;
      selected: string;
    };
    ui: {
      iconDefault: string;
      iconActive: string;
      iconBrand: string;
    };
    severity: {
      critical: string;
      high: string;
      medium: string;
      low: string;
    },
    status: {
      new: string,       // Bright Cyan (Distinct from Severity)
      assigned: string,  // Soft Lavender
      in_progress: string,// Your Deep Blue/Violet
      pending: string,    // Muted Slate-Blue
      resolved: string,   // Clean Green
      closed: string,     // Dim Grey
    },
  }

  interface PaletteOptions {
    border?: {
      light: string;
      main: string;
      strong: string;
      focus: string;
    };
    glow?: string;
    interactive?: {
      hover: string;
      selected: string;
    };
    ui?: {
      iconDefault: string;
      iconActive: string;
      iconBrand: string;
    };
    severity?: {
      critical: string;
      high: string;
      medium: string;
      low: string;
    };
    status: {
      new: string,       // Bright Cyan (Distinct from Severity)
      assigned: string,  // Soft Lavender
      in_progress: string,// Your Deep Blue/Violet
      pending: string,    // Muted Slate-Blue
      resolved: string,   // Clean Green
      closed: string,     // Dim Grey
    },
  }
}

// 2. Dark Theme Options
const createDarkThemeOptions = (): ThemeOptions => {
  const c = colors.dark;

  return {
    palette: {
      mode: 'dark' as PaletteMode,
      primary: {
        main: c.primary[500],
        light: c.primary[300],
        dark: c.primary[700],
        text: c.primary.text,
        glow: c.primary.glow,
        contrastText: c.primary.contrast
      },
      secondary: {
        main: c.primary[500],
        light: c.primary[300],
        dark: c.primary[700],
        contrastText: c.primary.contrast
      },
      error: {
        main: c.error[500],
        light: c.error.text,
        dark: c.error[500],
        contrastText: '#FFFFFF'
      },
      warning: {
        main: c.warning[500],
        light: c.warning.text,
        dark: c.warning[500],
        contrastText: '#000000'
      },
      info: {
        main: c.info[500],
        light: c.info.text,
        dark: c.info[500],
        contrastText: '#FFFFFF'
      },
      success: {
        main: c.success[500],
        light: c.success.text,
        dark: c.success[500],
        contrastText: '#FFFFFF'
      },
      severity:{
        critical: c.severity.critical,
        //critical: c.error[500],
        high: c.severity.high,
        medium: c.severity.medium,
        low: c.severity.low,
      },
      status: {
        new: c.status.new,             // Bright Cyan (Distinct from Severity)
        assigned: c.status.assigned,   // Soft Lavender
        in_progress: c.status.in_progress, // Your Deep Blue/Violet
        pending: c.status.pending,     // Muted Slate-Blue
        resolved: c.status.resolved,   // Clean Green
        closed: c.status.closed,       // Dim Grey
      },
      grey: {
        50: '#FAFAFA', 100: '#F4F4F5', 200: '#E4E4E7', 300: '#D4D4D8',
        400: '#A1A1AA', 500: '#71717A', 600: '#52525B', 700: '#3F3F46',
        800: '#27272A', 900: '#18181B',
      },
      text: {
        primary: c.text.primary,
        secondary: c.text.secondary,
        disabled: c.text.disabled,
        tertiary: c.text.tertiary,
        brand: c.text.brand,
      },
      background: {
        default: c.background.primary,
        paper: c.background.paper,
        secondary: c.background.secondary,
        tertiary: c.background.tertiary,
        elevated: c.background.elevated,
        inset: c.background.inset,
      },
      divider: c.border.light,
      border: c.border,
      glow: c.primary.glow,
      interactive: {
        hover: c.interactive.hover,
        selected: c.interactive.selected,
      },
      ui: {
        iconDefault: c.text.tertiary,
        iconActive: c.text.secondary,
        iconBrand: c.primary[500],
      },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: '"Inter", "system-ui", sans-serif',

      // PAGE TITLES
      h1: { fontWeight: 800, fontSize: '2.5rem', letterSpacing: '-0.02em', lineHeight: 1.2 },
      h2: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.02em', lineHeight: 1.2 },
      h3: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em', lineHeight: 1.3 },
      h4: { fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.01em', lineHeight: 1.4 },
      h5: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.5 },
      h6: { fontWeight: 600, fontSize: '1rem', letterSpacing: '-0.01em' },

      // SUBTITLES (For Section Headers)
      subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5, color: c.text.primary },
      subtitle2: {
        fontSize: '0.75rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: c.text.tertiary
      },

      // BODY TEXT
      body1: { fontSize: '1rem', lineHeight: 1.5, color: c.text.primary },
      body2: { fontSize: '0.875rem', lineHeight: 1.6, color: c.text.secondary },

      // SMALL TEXT
      caption: {
        fontFamily: '"JetBrains Mono", monospace', // Mono for data/metadata
        fontSize: '0.75rem',
        color: c.text.tertiary
      },
      overline: {
        fontSize: '0.65rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: c.text.disabled
      },

      button: { fontWeight: 600, textTransform: 'none' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Global scrollbar styles for all elements
          '*': {
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: c.background.tertiary,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: c.border.strong,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: c.text.secondary,
            },
            scrollbarWidth: 'thin',
            scrollbarColor: `${c.border.strong} ${c.background.tertiary}`,
          },
          html: {
            scrollbarWidth: 'thin',
            scrollbarColor: `${c.border.strong} ${c.background.tertiary}`,
          },
          body: {
            backgroundColor: c.background.primary,
            color: c.text.primary,
            // Global Focus Ring logic
            '& :focus-visible': {
              outline: `2px solid ${c.border.focus}`,
              outlineOffset: '2px',
            },
            // Motion support
            '@media (prefers-reduced-motion: reduce)': {
              '& *, & *::before, & *::after': {
                animationDuration: '0.01ms !important',
                animationIterationCount: '1 !important',
                transitionDuration: '0.01ms !important',
                scrollBehavior: 'auto !important',
              },
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: 'none',
            transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), border-color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': { boxShadow: 'none' },
            '&.Mui-focusVisible': {
              outline: 'none',
              boxShadow: `0 0 0 2px ${c.background.primary}, 0 0 0 4px ${c.border.focus}`,
            },
          },
          containedPrimary: {
            backgroundColor: c.primary[500],
            '&:hover': {
              backgroundColor: `oklch(from ${c.primary[500]} calc(l + 0.07) c h)`
            },
            '&:active': {
              backgroundColor: `oklch(from ${c.primary[500]} calc(l - 0.05) c h)`
            }
          },
          outlined: {
            borderColor: c.border.main,
            '&:hover': {
              backgroundColor: `oklch(from ${c.primary[500]} l c h / 0.08)`,
              borderColor: c.primary[500]
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 600,
            fontSize: '0.65rem',
            backgroundColor: c.background.tertiary,
            color: c.text.secondary,
            position: 'relative',
            border: `1px solid oklch(from ${c.background.tertiary} calc(l + 0.05) c h)`,
            // 44px Touch Target Expansion
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '44px',
              height: '44px',
              transform: 'translate(-50%, -50%)',
            },
          },
          colorPrimary: {
            backgroundColor: c.primary.glow,
            color: c.primary.text,
            border: `1px solid oklch(from ${c.primary[500]} l c h / 0.3)`,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 44, // WCAG Touch Target
            borderBottom: `1px solid oklch(from ${c.background.primary} calc(l + 0.05) c h)`,
          },
          indicator: {
            height: 2,
            backgroundColor: c.primary[500],
            boxShadow: `0 0 10px oklch(from ${c.primary[500]} l c h / 0.4)`,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 44, // WCAG Touch Target
            fontSize: '0.8rem',
            textTransform: 'none',
            color: c.text.tertiary,
            transition: 'color 0.2s ease',
            '&:hover': {
              color: c.text.primary,
              backgroundColor: `oklch(from ${c.primary[500]} l c h / 0.05)`,
            },
            '&.Mui-selected': {
              color: c.primary.text,
              fontWeight: 600,
            }
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 10,
            backgroundColor: c.background.secondary,
            border: `1px solid oklch(from ${c.background.secondary} calc(l + 0.04) c h)`,
            '&.MuiPaper-elevation2': {
              backgroundColor: `oklch(from ${c.background.secondary} calc(l + 0.03) c h)`,
            }
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            boxShadow: '0 24px 48px -8px oklch(0% 0 0 / 70%), 0 0 0 1px oklch(100% 0 0 / 6%)',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            boxShadow: '0 8px 24px -4px oklch(0% 0 0 / 50%), 0 0 0 1px oklch(100% 0 0 / 6%)',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            boxShadow: '0 8px 24px -4px oklch(0% 0 0 / 50%), 0 0 0 1px oklch(100% 0 0 / 6%)',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: `oklch(from ${c.background.primary} calc(l + 0.05) c h)`,
            '&::before, &::after': {
              borderColor: `oklch(from ${c.background.primary} calc(l + 0.05) c h)`,
            }
          },
        },
      },
    },
  };
};

// 3. Light Theme Options
const createLightThemeOptions = (): ThemeOptions => {
  const c = colors.light;

  return {
    palette: {
      mode: 'light' as PaletteMode,
      primary: {
        main: c.primary[500],
        light: c.primary[300],
        dark: c.primary[700],
        contrastText: c.primary.contrast
      },
      secondary: {
        main: c.accent[500],
        light: c.accent[300],
        dark: c.accent[700],
        contrastText: c.accent.contrast
      },
      error: {
        main: c.error[500],
        light: c.error[50],
        dark: c.error[500],
        contrastText: c.error.contrast
      },
      warning: {
        main: c.warning[500],
        light: c.warning[50],
        dark: c.warning[500],
        contrastText: '#000000'
      },
      info: {
        main: c.info[500],
        light: c.info[50],
        dark: c.info[500],
        contrastText: '#FFFFFF'
      },
      success: {
        main: c.success[500],
        light: c.success[50],
        dark: c.success[500],
        contrastText: '#FFFFFF'
      },
      status: {
        new: c.status.new,             // Bright Cyan (Distinct from Severity)
        assigned: c.status.assigned,   // Soft Lavender
        in_progress: c.status.in_progress, // Your Deep Blue/Violet
        pending: c.status.pending,     // Muted Slate-Blue
        resolved: c.status.resolved,   // Clean Green
        closed: c.status.closed,       // Dim Grey
      },
      grey: c.grey,
      text: {
        primary: c.text.primary,
        secondary: c.text.secondary,
        disabled: c.text.disabled,
        tertiary: c.text.tertiary,
        brand: c.text.brandLight,
      },
      background: {
        default: c.background.primary,
        paper: c.background.paper,
        secondary: c.background.secondary,
        tertiary: c.background.tertiary,
        elevated: c.background.elevated,
        inset: c.background.inset,
      },
      divider: c.border.light,
      border: c.border,
      glow: c.interactive.focus,
      interactive: {
        hover: c.interactive.hover,
        selected: c.interactive.selected,
      },
      ui: {
        iconDefault: c.ui.iconDefault,
        iconActive: c.ui.iconActive,
        iconBrand: c.ui.iconBrand,
      },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: '"Inter", "system-ui", sans-serif',

      // PAGE TITLES
      h1: { fontWeight: 800, fontSize: '2.5rem', letterSpacing: '-0.02em', lineHeight: 1.2 },
      h2: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.02em', lineHeight: 1.2 },
      h3: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em', lineHeight: 1.3 },
      h4: { fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.01em', lineHeight: 1.4 },
      h5: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.5 },
      h6: { fontWeight: 600, fontSize: '1rem', letterSpacing: '-0.01em' },

      // SUBTITLES (For Section Headers)
      subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5, color: c.text.primary },
      subtitle2: {
        fontSize: '0.75rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: c.text.tertiary
      },

      // BODY TEXT
      body1: { fontSize: '1rem', lineHeight: 1.5, color: c.text.primary },
      body2: { fontSize: '0.875rem', lineHeight: 1.6, color: c.text.secondary },

      // SMALL TEXT
      caption: {
        fontFamily: '"JetBrains Mono", monospace', // Mono for data/metadata
        fontSize: '0.75rem',
        color: c.text.tertiary
      },
      overline: {
        fontSize: '0.65rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: c.text.disabled
      },

      button: { fontWeight: 600, textTransform: 'none' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Global scrollbar styles for all elements
          '*': {
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: c.background.tertiary,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: c.border.strong,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: c.text.secondary,
            },
            scrollbarWidth: 'thin',
            scrollbarColor: `${c.border.strong} ${c.background.tertiary}`,
          },
          html: {
            scrollbarWidth: 'thin',
            scrollbarColor: `${c.border.strong} ${c.background.tertiary}`,
          },
          body: {
            backgroundColor: c.background.primary,
            color: c.text.primary,
            '@media (prefers-reduced-motion: reduce)': {
              '& *, & *::before, & *::after': {
                animationDuration: '0.01ms !important',
                transitionDuration: '0.01ms !important',
              },
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: 'none',
            '&:hover': {boxShadow: 'none'},
            '&.Mui-focusVisible': {
              outline: 'none',
              boxShadow: `0 0 0 2px ${c.background.primary}, 0 0 0 4px ${c.border.focus}`,
            },
            containedPrimary: {
              backgroundColor: c.primary[600],
              '&:hover': {backgroundColor: c.primary[500]},
            },
            outlined: {
              borderColor: c.border.main,
              '&:hover': {
                backgroundColor: `oklch(from ${c.primary[500]} l c h / 0.08)`,
                borderColor: c.primary[500]
              },
              // ADD ACTIVE STATE: Darken the tint on press
              '&:active': {
                backgroundColor: `oklch(from ${c.primary[500]} l c h / 0.15)`,
                transform: 'scale(0.98)', // Adds physical "push" feel
              }
            },
            transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 600,
            fontSize: '0.65rem',
            backgroundColor: c.background.tertiary,
            color: c.text.secondary,
            position: 'relative',
            border: `1px solid oklch(from ${c.background.tertiary} calc(l + 0.05) c h)`,
            transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',

            // THE HOVER STATE: Lighten the background and border
            '&:hover': {
              backgroundColor: `oklch(from ${c.background.tertiary} calc(l + 0.05) c h)`,
              borderColor: `oklch(from ${c.background.tertiary} calc(l + 0.1) c h)`,
              color: c.text.primary,
              cursor: 'pointer',
            },

            '&:active': {
              transform: 'scale(0.96)',
              filter: 'brightness(0.8)',
            },

            // 44px Touch Target
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '44px',
              height: '44px',
              transform: 'translate(-50%, -50%)',
            },
          },
          colorPrimary: {
            //backgroundColor: c.primary.text,
            //color: c.primary.text,
            border: `1px solid oklch(from ${c.primary[500]} l c h / 0.3)`,
            // Primary color hover: Increase the chroma (vibrancy) or lightness
            '&:hover': {
              backgroundColor: `oklch(from ${c.primary[500]} calc(l + 0.05) c h / 0.25)`,
              borderColor: `oklch(from ${c.primary[500]} l c h / 0.5)`,
            }
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44 },
        }
      },
      MuiTab: {
        styleOverrides: {
          root: { minHeight: 44, transition: 'color 150ms ease, background-color 150ms ease', },
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 10,
            backgroundColor: c.background.secondary,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            boxShadow: '0 24px 48px -8px oklch(0% 0 0 / 20%), 0 0 0 1px oklch(0% 0 0 / 6%)',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            boxShadow: '0 8px 24px -4px oklch(0% 0 0 / 12%), 0 0 0 1px oklch(0% 0 0 / 5%)',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            boxShadow: '0 8px 24px -4px oklch(0% 0 0 / 12%), 0 0 0 1px oklch(0% 0 0 / 5%)',
          },
        },
      },
    },
  };
};

// 4. Create and export themes
const lightTheme = createTheme({
  cssVariables: true,
  ...createLightThemeOptions(),
});

const darkTheme = createTheme({
  cssVariables: true,
  ...createDarkThemeOptions(),
});

export const getTheme = (mode: 'light' | 'dark'): Theme => mode === 'light' ? lightTheme : darkTheme;
export { lightTheme, darkTheme };
export default darkTheme;